// src/features/procedures/pages/ProcedureDetailsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useProcedures } from '../hooks/useProcedures';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { usePermissions } from '../../auth/hooks/usePermissions';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Components
import ProcedureStepCard from '../components/ProcedureStepCard';

// Utils
import { UUIDDisplay } from '../../../utils/formatters/uuid';
import PageHeader from '../../../components/common/PageHeader';

export default function ProcedureDetailsPage() {
  const { t } = useTranslation(['procedures', 'common']);
  const navigate = useNavigate();
  const { procedureId } = useParams();
  const { showError } = useSnackbar();
  const permissions = usePermissions();
  const { navigateBack } = useNavigationStack();

  // State
  const [procedure, setProcedure] = useState(null);
  const [loading, setLoading] = useState(true);

  // API hooks
  const {
    fetchProcedureById,
    error,
    clearError,
  } = useProcedures();

  const loadProcedureData = useCallback(async () => {
    if (!procedureId) return;

    setLoading(true);
    try {
      const result = await fetchProcedureById(procedureId);
      if (result.success) {
        setProcedure(result.data.data);
      } else {
        showError(t('procedures:errors.load_failed'));
      }
    } catch (error) {
      showError(t('procedures:errors.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [procedureId, fetchProcedureById, showError, t]);

  // Load procedure data
  useEffect(() => {
    loadProcedureData();
  }, [loadProcedureData]);

  const handleBack = useCallback(() => {
    navigateBack('/procedures');
  }, [navigateBack]);

  const handleEdit = useCallback(() => {
    navigate(`/procedures/${procedureId}/edit`);
  }, [navigate, procedureId]);

  // Check permissions
  const canUpdate = permissions.hasScope('procedure:update');

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found state
  if (!procedure) {
    return (
      <Box>
        <PageHeader
          onBack={handleBack}
          title={t('procedures:messages.procedure_not_found')}
        />
        <Alert severity="error">
          {t('procedures:messages.procedure_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={handleBack}
        title={procedure.name}
        badges={<>
          {procedure.is_system_procedure && (
            <Chip
              icon={<SecurityIcon />}
              label={t('procedures:labels.system')}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
          <Chip
            icon={procedure.is_active ? <ActiveIcon /> : <InactiveIcon />}
            label={procedure.is_active ? t('procedures:labels.active') : t('procedures:labels.inactive')}
            size="small"
            color={procedure.is_active ? 'success' : 'default'}
            variant="outlined"
          />
        </>}
        subtitle={<>{t('common:labels.id')} <UUIDDisplay uuid={procedure.procedure_id} /></>}
        actions={<>
          {canUpdate && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              {t('procedures:actions.edit')}
            </Button>
          )}
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Box display="flex" flexDirection="column" gap={3}>
        {/* Procedure Details */}
        <Card>
          <CardContent>
            {procedure.description && (
              <Typography variant="body1" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
                {procedure.description}
              </Typography>
            )}
            <Box display="flex" gap={3} flexWrap="wrap" color="text.secondary">
              <Typography variant="body2">
                {t('procedures:labels.total_steps')}: <strong>{procedure.steps?.length || 0}</strong>
              </Typography>
              <Typography variant="body2" component="div">
                {t('procedures:labels.created')}: <LocalizedDateDistance date={procedure.created_at} />
              </Typography>
              <Typography variant="body2" component="div">
                {t('procedures:labels.last_updated')}: <LocalizedDateDistance date={procedure.updated_at} />
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Procedure Steps */}
        <Box minWidth={0}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('procedures:labels.procedure_steps')} ({procedure.steps?.length || 0})
              </Typography>
              <Divider sx={{ my: 2 }} />

              {!procedure.steps || procedure.steps.length === 0 ? (
                <Alert severity="info">
                  {t('procedures:messages.no_steps')}
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  {procedure.steps
                    .sort((a, b) => a.step_order - b.step_order)
                    .map((step, index) => (
                      <ProcedureStepCard
                        key={step.step_id}
                        step={step}
                        stepNumber={index + 1}
                      />
                    ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}